import { Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, fromEvent, interval, merge, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { IdleWarningDialogComponent } from '../app/utility/idle-warning-dialog/idle-warning-dialog.component';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const WARNING_DURATION_MS = 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

/**
 * Forces a logout after a fixed idle period, independent of whether the access
 * token would otherwise be silently refreshed on the next API call (WCHRON-566).
 */
@Injectable({ providedIn: 'root' })
export class IdleTimeoutService {
  private lastActivity = Date.now();
  private warningShown = false;
  private onTimeout?: () => void;

  private activitySub?: Subscription;
  private tickSub?: Subscription;
  private warningDialogRef?: MatDialogRef<IdleWarningDialogComponent, 'stay' | 'logout'>;
  private readonly secondsRemaining$ = new BehaviorSubject<number>(0);

  constructor(private dialog: MatDialog, private zone: NgZone) { }

  start(onTimeout: () => void): void {
    this.stop();
    this.onTimeout = onTimeout;
    this.lastActivity = Date.now();
    this.warningShown = false;

    this.zone.runOutsideAngular(() => {
      this.activitySub = merge(...ACTIVITY_EVENTS.map(evt => fromEvent(document, evt)))
        .pipe(throttleTime(1000))
        .subscribe(() => {
          if (!this.warningShown) this.lastActivity = Date.now();
        });

      this.tickSub = interval(1000).subscribe(() => this.zone.run(() => this.tick()));
    });
  }

  stop(): void {
    this.activitySub?.unsubscribe();
    this.tickSub?.unsubscribe();
    this.activitySub = undefined;
    this.tickSub = undefined;
    this.warningDialogRef?.close();
    this.warningDialogRef = undefined;
    this.warningShown = false;
  }

  private tick(): void {
    const remainingMs = IDLE_TIMEOUT_MS - (Date.now() - this.lastActivity);

    if (remainingMs <= 0) {
      const timeout = this.onTimeout;
      this.stop();
      timeout?.();
      return;
    }

    if (remainingMs <= WARNING_DURATION_MS) {
      this.secondsRemaining$.next(Math.ceil(remainingMs / 1000));
      if (!this.warningShown) {
        this.warningShown = true;
        this.showWarningDialog();
      }
    }
  }

  private showWarningDialog(): void {
    this.warningDialogRef = this.dialog.open(IdleWarningDialogComponent, {
      disableClose: true,
      minWidth: 400,
      data: { secondsRemaining$: this.secondsRemaining$.asObservable() }
    });

    this.warningDialogRef.afterClosed().subscribe(result => {
      this.warningDialogRef = undefined;
      if (result === 'stay') {
        this.warningShown = false;
        this.lastActivity = Date.now();
      } else if (result === 'logout') {
        const timeout = this.onTimeout;
        this.stop();
        timeout?.();
      }
      // Any other result (e.g. dialog closed by stop()) is handled by the caller already.
    });
  }
}
